// Errors from third-party SDK paths (Amplitude session replay → IndexedDB, LaunchDarkly's
// analytics beacon) that we cannot fix and that pollute Sentry alerting.
const THIRD_PARTY_NOISE_PATTERNS = [
  /FILE_ERROR_NO_SPACE/i,
  /indexeddb.+leveldb/i,
  /Connection is closing because of: IO error/i,
  /Failed to store session replay events in IndexedDB/i,
  /QuotaExceededError/i,
  /Failed to load 'https:\/\/events\.launchdarkly\.com/i, // LD beacon; this list drops, so anchor
]

// Unactionable transport failures (handled by the app). End-anchored fixed strings, not loose
// substrings — else "Failed to fetch incoming orders: …" / "Payload failed …" would drop too.
const TRANSPORT_NOISE_PATTERNS = [
  /\bfetch failed$/i, // undici
  /\bNetworkError when attempting to fetch resource\.?$/i, // Firefox
  /\bFailed to fetch$/i, // Chrome
  /\bLoad failed$/i, // Safari
  /\bThe network connection was lost\.?$/i, // iOS/Safari
  /\bServer returned an HTML page instead of tRPC data\b/i, // UnexpectedHtmlResponseError
]

// A stale JS chunk requested after a deploy, phrased per engine. Unanchored on purpose: these
// only fingerprint (main.tsx), never drop, so a miss costs issue churn and an over-match nothing.
const STALE_CHUNK_PATTERNS = [
  /\bFailed to fetch dynamically imported module\b/i, // Chrome
  /\berror loading dynamically imported module\b/i, // Firefox
  /\bImporting a module script failed\b/i, // Safari
  /\bFailed to load module script\b/i, // SPA fallback served index.html for a deleted chunk
  /\bUnable to preload CSS\b/i, // Vite, same vite:preloadError path
]

const getErrorMessage = (err: unknown): string | undefined => {
  if (err instanceof Error) return err.message
  if (typeof err === "string") return err
  const message = (err as { message?: unknown })?.message
  return typeof message === "string" ? message : undefined
}

// The status page's "Test Sentry" controls deliberately raise errors to confirm the Sentry
// pipeline is live (HAK-2136 / CORE-PB). They are integration tests, not product failures, so
// we tag them for triage — but never drop them, that would defeat the test. Marker lives on the
// Error object so detection never depends on the message string alone.
export const SENTRY_TEST_ERROR_MESSAGE = "Sentry test error"
const SENTRY_TEST_MARKER = "__hakioSentryTest"

export const createSentryTestError = (): Error => {
  const err = new Error(SENTRY_TEST_ERROR_MESSAGE)
  Object.defineProperty(err, SENTRY_TEST_MARKER, { value: true, enumerable: false })
  return err
}

export const isIntentionalSentryTest = (err: unknown): boolean => {
  if ((err as Record<string, unknown> | null | undefined)?.[SENTRY_TEST_MARKER] === true) {
    return true
  }
  return getErrorMessage(err) === SENTRY_TEST_ERROR_MESSAGE
}

export const isThirdPartyStorageNoise = (err: unknown): boolean => {
  const message = getErrorMessage(err)
  if (typeof message !== "string") return false
  return THIRD_PARTY_NOISE_PATTERNS.some((p) => p.test(message))
}

export const isStaleChunkImportFailure = (err: unknown): boolean => {
  const message = getErrorMessage(err)
  if (typeof message !== "string") return false
  return STALE_CHUNK_PATTERNS.some((p) => p.test(message))
}

// Checks the error and its `.cause` (tRPC wraps the transport error there).
export const isTransientTransportNoise = (err: unknown): boolean => {
  const messages = [getErrorMessage(err), getErrorMessage((err as { cause?: unknown })?.cause)]
  return messages.some(
    (m) => typeof m === "string" && TRANSPORT_NOISE_PATTERNS.some((p) => p.test(m)),
  )
}
